/**
 * ImageUploadModal
 * 支持最多 100 张图片上传，含：
 *  - 不压缩，直接上传原图（compressImage 已禁用并原样返回文件）：OCR 识别率与图片质量正相关，
 *    任何 JPEG 压缩都会降低识别率（尤其是深色背景白字、细小字体）。
 *    ※ 旧注释写的"Canvas 压缩 最大 1920px / JPEG 0.78"早已过时，排查 OCR 问题时不要被它误导。
 *  - 并行批量上传（每批 8 张，3 路并发）
 *  - 分阶段进度条（准备 / 上传双进度）
 *  - 失败重试
 *  - 补充上传模式（mode='supplement'：不触发完成任务）
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, ImagePlus, X, CheckCircle2, Loader,
  AlertTriangle, RotateCcw, ScanLine
} from 'lucide-react';
// 共享上传管线：压缩 / 并发池 / XHR 字节级进度（P2-10 统一，P3-15 字节进度）
import { compressImage, runPool, xhrUpload, playUploadDone } from '../utils/uploadPipeline';
import { lockBodyScroll } from '../utils/scrollLock';

// ─── 配置常量 ───────────────────────────────────────────────────────────────
const MAX_FILES          = 100;  // 每次最多选取张数
const COMPRESS_PARALLEL  = 6;    // 同时压缩张数（Canvas 并发）
const UPLOAD_BATCH_SIZE  = 8;    // 每次请求携带张数
const UPLOAD_PARALLEL    = 4;    // 并发请求数（最多同时 4×8=32 张在传）

// ─── 子组件：带进度条的单步进度 ──────────────────────────────────────────────
function ProgressBar({ label, done, total, failed = 0, active, suffix, pctOverride }) {
  // pctOverride：字节级进度（0-100），传入时进度条按字节走，计数文案仍显示张数
  const pct   = pctOverride != null
    ? Math.max(0, Math.min(100, Math.round(pctOverride)))
    : (total > 0 ? Math.round((done / total) * 100) : 0);
  const isDone = done >= total && total > 0 && !active;
  const color  = isDone ? 'var(--c-success)' : active ? 'var(--c-primary)' : 'var(--c-text-tertiary)';
  const barBg  = failed > 0
    ? 'linear-gradient(90deg, var(--c-success) 0%, var(--c-accent) 100%)'
    : isDone ? 'var(--c-success)' : 'var(--c-primary)';

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isDone
            ? <CheckCircle2 size={14} color="var(--c-success)" />
            : active
              ? <Loader size={14} color="var(--c-primary)" className="spin" />
              : <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--c-border)' }} />}
          <span style={{ fontSize: 13, fontWeight: 600, color }}>{label}</span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>
          {active || isDone ? `${done} / ${total}` : '待处理'}
          {failed > 0 && <span style={{ color: 'var(--c-danger)', marginLeft: 4 }}>· {failed} 失败</span>}
          {suffix ? <span style={{ color: 'var(--c-success)', marginLeft: 4 }}>{suffix}</span> : null}
        </span>
      </div>
      <div style={{ height: 7, background: 'var(--c-bg)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99, transition: 'width 0.25s ease',
          width: `${pct}%`, background: barBg
        }} />
      </div>
    </div>
  );
}

// ─── 主组件 ──────────────────────────────────────────────────────────────────
export default function BatchUploadModal({ groupUUID, mode = 'complete', onComplete, onClose }) {
  const navigate = useNavigate();
  // phase: select | compressing | uploading | done | partial | error
  const [phase, setPhase] = useState('select');
  const [rawFiles, setRawFiles] = useState([]);   // 用户原始选择
  const [previews, setPreviews] = useState([]);   // ObjectURL 列表
  const [stats, setStats] = useState({
    compressDone: 0, compressTotal: 0, savedBytes: 0,
    uploadDone: 0, uploadFailed: 0, uploadTotal: 0,
    uploadLoadedBytes: 0, uploadTotalBytes: 0   // P3-15：字节级上传进度
  });
  const [failedFiles, setFailedFiles] = useState([]); // 上传失败的（已压缩）File
  const [warning, setWarning] = useState('');
  const [errorMsg, setErrorMsg] = useState(''); // error phase 的具体原因
  const [completing, setCompleting] = useState(false);
  const fileInputRef = useRef(null);
  // 用 ref 记录所有创建过的 blob URL，确保卸载时全部 revoke（stale closure 问题）
  const allBlobUrlsRef = useRef([]);

  // 清理 ObjectURL 防泄漏：卸载时 revoke ref 里记录的所有 URL
  useEffect(() => () => {
    allBlobUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    allBlobUrlsRef.current = [];
  }, []); // eslint-disable-line

  // 弹窗打开期间锁定底层页面滚动，避免选图/键盘弹出时背景页跟着乱滚到底部。
  // 滚动容器是 body（见 index.css 里 html / body 的说明），用共用的 lockBodyScroll（带引用计数，多弹窗叠加也不会锁死）。
  useEffect(() => lockBodyScroll(), []);

  // ── 添加文件 ──────────────────────────────────────────────────────────────
  const addFiles = (selected) => {
    if (!selected.length) return;
    const next = [...rawFiles, ...selected].slice(0, MAX_FILES);
    const overflow = rawFiles.length + selected.length > MAX_FILES;
    setRawFiles(next);
    const newUrls = selected.slice(0, MAX_FILES - rawFiles.length).map(f => {
      const url = URL.createObjectURL(f);
      allBlobUrlsRef.current.push(url); // 记录，卸载时统一 revoke
      return url;
    });
    setPreviews(p => [...p, ...newUrls].slice(0, MAX_FILES));
    setWarning(overflow ? `已达上限 ${MAX_FILES} 张，多余已截断` : '');
  };

  const handleFileInput = (e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; };

  const removeFile = (i) => {
    const url = previews[i];
    URL.revokeObjectURL(url);
    // 同步从总记录里删除，避免卸载时再次 revoke 已释放的 URL
    allBlobUrlsRef.current = allBlobUrlsRef.current.filter(u => u !== url);
    setRawFiles(f => f.filter((_, j) => j !== i));
    setPreviews(p => p.filter((_, j) => j !== i));
    setWarning('');
  };

  // ── 核心：压缩 + 上传 Pipeline ────────────────────────────────────────────
  const run = useCallback(async (filesToProcess) => {
    const total = filesToProcess.length;

    // ── Phase 1: 压缩 ────────────────────────────────────────────────────
    setPhase('compressing');
    setStats({ compressDone: 0, compressTotal: total, savedBytes: 0, uploadDone: 0, uploadFailed: 0, uploadTotal: total, uploadLoadedBytes: 0, uploadTotalBytes: 0 });

    const compressed = new Array(total);
    let savedBytes = 0, compressDone = 0;

    const compressTasks = filesToProcess.map((file, idx) => async () => {
      const out = await compressImage(file);
      savedBytes += Math.max(0, file.size - out.size);
      compressDone++;
      compressed[idx] = out;
      setStats(s => ({ ...s, compressDone, savedBytes }));
    });

    await runPool(compressTasks, COMPRESS_PARALLEL);

    // ── Phase 2: 分批并发上传（XHR 字节级进度）──────────────────────────────
    setPhase('uploading');
    let uploadDone = 0, uploadFailed = 0;
    const failedBatch = [];

    // 切分为每 UPLOAD_BATCH_SIZE 张一批
    const batches = [];
    for (let i = 0; i < compressed.length; i += UPLOAD_BATCH_SIZE) {
      batches.push(compressed.slice(i, i + UPLOAD_BATCH_SIZE));
    }

    // P3-15：字节级进度。总字节固定，每批各自累计已传字节，汇总 sum(loaded)/sum(total)。
    // 各批乱序完成也只会让 loadedBytes 单调增长，进度条不回跳（不再用 Math.max(prev,cur)）。
    const uploadTotalBytes = compressed.reduce((s, f) => s + f.size, 0);
    const perBatchLoaded = new Array(batches.length).fill(0);
    setStats(s => ({ ...s, uploadLoadedBytes: 0, uploadTotalBytes }));

    // groupUUID 由调用方（WorkspacePage.openUploadFree）在打开弹窗时生成，
    // 同一弹窗生命周期内所有分批请求共用同一个 UUID，后端幂等建组。
    const token = localStorage.getItem('token');

    const uploadTasks = batches.map((batch, bi) => async () => {
      try {
        const responseText = await xhrUpload({
          url: '/api/images/upload-free',
          files: batch,
          token,
          // X-Expected-Count 告知服务端本批期望张数，服务端收到残包时可返回 400
          headers: { 'X-Upload-Group-Id': groupUUID, 'X-Expected-Count': String(batch.length) },
          timeout: 90000,   // 8 张 × 最大 15MB ≈ 120MB，4G 约 60s，留余量
          onProgress: (loaded) => {
            perBatchLoaded[bi] = loaded;
            const loadedBytes = perBatchLoaded.reduce((a, b) => a + b, 0);
            setStats(s => ({ ...s, uploadLoadedBytes: loadedBytes }));
          }
        });

        // 用服务端实际保存数校验，防止弱网截断时服务端只收到部分文件却返回 200
        let actualCount = batch.length;
        try {
          const result = JSON.parse(responseText);
          if (typeof result.count === 'number') actualCount = result.count;
        } catch { /* JSON 解析失败时按全部成功处理 */ }

        if (actualCount < batch.length) {
          // 服务端保存数少于发送数：把缺失的文件加入重试队列
          const missingCount = batch.length - actualCount;
          uploadFailed += missingCount;
          uploadDone   += actualCount;
          // 把该批最后 missingCount 张加入失败队列（服务端按顺序落盘，尾部最可能丢失）
          failedBatch.push(...batch.slice(actualCount));
          perBatchLoaded[bi] = 0; // 进度回退，反映真实成功量
          const loadedBytes = perBatchLoaded.reduce((a, b) => a + b, 0);
          setStats(s => ({ ...s, uploadLoadedBytes: loadedBytes }));
          setErrorMsg('部分图片未能保存（网络不稳定），请重试失败的图片');
        } else {
          uploadDone += batch.length;
        }
      } catch (err) {
        uploadFailed += batch.length;
        failedBatch.push(...batch); // 已压缩的 File，重试时直接用
        // 该批失败：把它的字节从已传里回退，进度反映真实成功量
        perBatchLoaded[bi] = 0;
        const loadedBytes = perBatchLoaded.reduce((a, b) => a + b, 0);
        setStats(s => ({ ...s, uploadLoadedBytes: loadedBytes }));
        // 解析具体错误原因（取最后一次失败的，通常是同一原因）
        let reason = '请检查网络后重试';
        if (err.status === 403) {
          try {
            const body = JSON.parse(err.responseText || '{}');
            reason = body.error || '当前无上传权限';
            if (body.code) reason += `\n错误码：${body.code}`;
          } catch { reason = '当前无上传权限'; }
        } else if (err.status === 400) {
          try {
            const body = JSON.parse(err.responseText || '{}');
            if (body.code === 'UPLOAD_INCOMPLETE') {
              reason = `上传不完整（期望 ${body.expected} 张，服务端仅收到 ${body.received} 张）\n错误码：${body.code}`;
            } else {
              reason = body.error || '上传不完整，请重试';
              if (body.code) reason += `\n错误码：${body.code}`;
            }
          } catch { reason = '上传不完整，请重试'; }
        } else if (err.status === 413) reason = '图片过大（单张最大 15MB），请压缩后重试';
        else if (err.message === 'timeout') reason = '上传超时，请检查网络信号';
        else if (err.message === 'network') reason = '网络连接失败，请检查网络';
        setErrorMsg(reason);
      }
      setStats(s => ({ ...s, uploadDone, uploadFailed }));
    });

    await runPool(uploadTasks, UPLOAD_PARALLEL);

    setFailedFiles(failedBatch);
    const nextPhase = uploadFailed === 0 ? 'done'
      : uploadFailed === total ? 'error' : 'partial';
    // 全部成功时：播放提示音 + 发一条汇总通知（替代每批各发一条）
    if (nextPhase === 'done') {
      playUploadDone();
      try {
        const token = localStorage.getItem('token');
        fetch('/api/images/notify-upload-done', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Upload-Group-Id': groupUUID
          }
        });
      } catch { /* 通知失败不影响上传结果 */ }
    }
    setPhase(nextPhase);
  }, [groupUUID]);

  // ── 开始（选图后点击）────────────────────────────────────────────────────
  const handleStart = () => {
    if (!rawFiles.length) { setWarning('请先选择图片'); return; }
    run(rawFiles);
  };

  // ── 重试（失败的已压缩文件直接再传，跳过重压缩会更快）─────────────────────
  const handleRetry = () => {
    if (!failedFiles.length) return;
    // failedFiles 已是压缩后的小文件，compressImage 会因 < COMPRESS_SKIP_SIZE 跳过
    run(failedFiles);
  };

  // ── 完成任务 ──────────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!onComplete) return;
    setCompleting(true);
    try { await onComplete(); } finally { setCompleting(false); }
  };

  // ── 格式化字节 ────────────────────────────────────────────────────────────
  const fmtSize = (b) =>
    b < 1024 ? `${b}B`
    : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)}KB`
    : `${(b / 1024 / 1024).toFixed(1)}MB`;

  const isProcessing = phase === 'compressing' || phase === 'uploading';

  // ─── RENDER ──────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.62)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
    }}>
      <div style={{
        background: 'var(--c-surface)',
        borderRadius: '22px 22px 0 0',
        width: '100%', maxWidth: 520,
        maxHeight: '93vh', overflowY: 'auto',
        padding: '20px 16px 32px'
      }}>

        {/* ── 标题栏 ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Camera size={20} color="var(--c-primary)" />
            <span style={{ fontSize: 16, fontWeight: 700 }}>
              {mode === 'supplement' ? '补充上传凭证' : mode === 'upload' ? '上传图片' : '上传工作凭证'}
            </span>
          </div>
          <button onClick={onClose} disabled={isProcessing}
            style={{ background: 'none', border: 'none', cursor: isProcessing ? 'not-allowed' : 'pointer',
                     padding: 4, opacity: isProcessing ? 0.35 : 1 }}>
            <X size={20} color="var(--c-text-secondary)" />
          </button>
        </div>

        {/* 文件计数 */}
        {rawFiles.length > 0 && phase === 'select' && (
          <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--c-primary)', fontWeight: 700, marginBottom: 10 }}>
            {rawFiles.length} / {MAX_FILES}
          </div>
        )}

        {/* ══════════════ SELECT PHASE ══════════════ */}
        {phase === 'select' && (
          <>
            {/* 上传区 */}
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${rawFiles.length > 0 ? 'var(--c-primary)' : 'var(--c-border)'}`,
                borderRadius: 16, padding: '22px 16px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 10, transition: 'all 0.15s',
                background: rawFiles.length > 0 ? 'var(--c-primary-light)' : 'var(--c-bg)'
              }}
            >
              <ImagePlus size={32}
                color={rawFiles.length > 0 ? 'var(--c-primary)' : 'var(--c-text-tertiary)'}
                style={{ margin: '0 auto 8px', display: 'block' }} />
              <div style={{ fontSize: 15, fontWeight: 700,
                color: rawFiles.length > 0 ? 'var(--c-primary)' : 'var(--c-text-secondary)' }}>
                {rawFiles.length > 0 ? '点击继续添加图片' : '点击选择图片'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 4 }}>
                JPG / PNG / WEBP · 每张最大 15MB · 最多 {MAX_FILES} 张
              </div>
              <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 3 }}>
                上传前会自动压缩，节省流量，速度更快
              </div>
              <div style={{
                marginTop: 10, padding: '7px 10px',
                background: 'rgba(255,149,0,0.10)', borderRadius: 8,
                fontSize: 11, color: 'var(--c-accent)', lineHeight: 1.6, textAlign: 'left',
              }}>
                ⚠️ 请直接从<strong>手机相册</strong>选择截图上传<br />
                微信发图会自动压缩，转发后保存的图片画质太低，OCR 无法识别号码
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple
              style={{ display: 'none' }} onChange={handleFileInput} />

            {/* 预览网格（5 列，可滚动） */}
            {previews.length > 0 && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 7,
                marginBottom: 12, maxHeight: '30vh', overflowY: 'auto'
              }}>
                {previews.map((url, i) => (
                  <div key={i} style={{ position: 'relative', aspectRatio: '1' }}>
                    <img src={url} alt="" loading="lazy" style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      borderRadius: 9, border: '1px solid var(--c-border)', display: 'block'
                    }} />
                    <button onClick={() => removeFile(i)} style={{
                      position: 'absolute', top: -6, right: -6,
                      width: 20, height: 20, borderRadius: '50%', padding: 0,
                      background: 'rgba(255,59,48,0.9)', border: '1.5px solid white',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <X size={10} color="white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {warning && (
              <div style={{
                background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)',
                borderRadius: 10, padding: '9px 12px', fontSize: 13,
                color: 'var(--c-accent)', marginBottom: 10
              }}>{warning}</div>
            )}

            {/* 主按钮 */}
            <button
              onClick={handleStart}
              disabled={rawFiles.length === 0}
              style={{
                width: '100%', height: 52, borderRadius: 16,
                background: rawFiles.length > 0 ? 'var(--c-primary)' : 'var(--c-border)',
                color: rawFiles.length > 0 ? 'white' : 'var(--c-text-secondary)',
                border: 'none', fontSize: 15, fontWeight: 700,
                cursor: rawFiles.length > 0 ? 'cursor' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginBottom: 10, transition: 'all 0.15s'
              }}
            >
              <Camera size={16} />
              {rawFiles.length > 0 ? `上传 ${rawFiles.length} 张图片` : '请先选择图片'}
            </button>
          </>
        )}

        {/* ══════════════ COMPRESSING / UPLOADING ══════════════ */}
        {(phase === 'compressing' || phase === 'uploading') && (
          <div style={{ padding: '8px 0 4px' }}>
            <ProgressBar
              label="压缩图片"
              done={stats.compressDone}
              total={stats.compressTotal}
              active={phase === 'compressing'}
              suffix={stats.savedBytes > 10240 ? `节省 ${fmtSize(stats.savedBytes)}` : ''}
            />
            <ProgressBar
              label="上传图片"
              done={stats.uploadDone}
              total={stats.uploadTotal}
              failed={stats.uploadFailed}
              active={phase === 'uploading'}
              pctOverride={stats.uploadTotalBytes > 0
                ? (stats.uploadLoadedBytes / stats.uploadTotalBytes) * 100
                : undefined}
            />
            <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', textAlign: 'center', marginTop: 8 }}>
              {phase === 'compressing'
                ? `正在压缩 ${stats.compressDone}/${stats.compressTotal} 张，请稍候...`
                : `${UPLOAD_PARALLEL} 路并发上传中，请保持应用在前台...`}
            </div>
          </div>
        )}

        {/* ══════════════ DONE ══════════════ */}
        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
            <CheckCircle2 size={56} color="var(--c-success)"
              style={{ margin: '0 auto 12px', display: 'block' }} />
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={18} color="var(--c-success)" strokeWidth={2} /> 上传完成</div>
            <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 4 }}>
              {stats.uploadDone} 张图片已保存，后台正在识别手机号
            </div>
            {stats.savedBytes > 10240 && (
              <div style={{ fontSize: 12, color: 'var(--c-success)', marginBottom: 8 }}>
                压缩节省了 {fmtSize(stats.savedBytes)} 流量
              </div>
            )}

            {/* OCR 引导 banner */}
            <div style={{
              background: 'rgba(10,132,255,0.07)',
              border: '1px solid rgba(10,132,255,0.2)',
              borderRadius: 12,
              padding: '12px 14px',
              margin: '14px 0 4px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              textAlign: 'left',
            }}>
              <ScanLine size={18} color="var(--c-primary)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.5 }}>
                识别完成后将推送通知，可在底部 <strong style={{ color: 'var(--c-primary)' }}>OCR</strong> 页面查看结果
              </div>
              <button
                onClick={() => { onClose(); navigate('/history'); }}
                style={{
                  flexShrink: 0,
                  background: 'var(--c-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                去查看
              </button>
            </div>

            {mode === 'complete' ? (
              <button onClick={handleComplete} disabled={completing} style={{
                width: '100%', height: 52, borderRadius: 16, border: 'none',
                background: 'var(--c-success)', color: 'white',
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginTop: 12
              }}>
                {completing
                  ? <><Loader size={16} className="spin" /> 完成中...</>
                  : <><CheckCircle2 size={16} /> 确认完成任务</>}
              </button>
            ) : (
              <button onClick={onClose} style={{
                width: '100%', height: 50, borderRadius: 16, border: 'none',
                background: 'var(--c-bg)', color: 'var(--c-text-secondary)',
// eslint-disable-next-line no-dupe-keys
                border: '1px solid var(--c-border)',
                fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 12
              }}>关闭</button>
            )}
          </div>
        )}

        {/* ══════════════ PARTIAL (部分失败) ══════════════ */}
        {phase === 'partial' && (
          <div>
            <div style={{
              display: 'flex', gap: 10, padding: '12px 14px',
              background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)',
              borderRadius: 14, marginBottom: 14
            }}>
              <AlertTriangle size={20} color="var(--c-accent)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-accent)' }}>部分图片上传失败</div>
                <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 3 }}>
                  成功 <strong>{stats.uploadDone}</strong> 张 · 失败 <strong style={{ color: 'var(--c-danger)' }}>{stats.uploadFailed}</strong> 张
                </div>
                {errorMsg && <div style={{ fontSize: 12, color: 'var(--c-danger)', marginTop: 4, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{errorMsg}</div>}
              </div>
            </div>
            <button onClick={handleRetry} style={{
              width: '100%', height: 48, borderRadius: 14,
              background: 'var(--c-accent)', color: 'white', border: 'none',
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginBottom: 10
            }}>
              <RotateCcw size={15} /> 重试 {stats.uploadFailed} 张失败图片
            </button>
            {mode === 'complete' && (
              <button onClick={handleComplete} disabled={completing} style={{
                width: '100%', height: 48, borderRadius: 14,
                background: 'var(--c-primary)', color: 'white', border: 'none',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}>
                {completing
                  ? <><Loader size={14} className="spin" /> 完成中...</>
                  : <><CheckCircle2 size={14} /> 忽略失败，直接完成任务</>}
              </button>
            )}
          </div>
        )}

        {/* ══════════════ ERROR (全部失败) ══════════════ */}
        {phase === 'error' && (
          <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
            <AlertTriangle size={52} color="var(--c-danger)"
              style={{ margin: '0 auto 12px', display: 'block' }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-danger)', marginBottom: 6 }}>
              上传失败
            </div>
            <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 20, whiteSpace: 'pre-wrap', textAlign: 'left', background: 'var(--c-bg)', borderRadius: 10, padding: '10px 12px', lineHeight: 1.7 }}>
              全部 {stats.uploadTotal} 张上传失败\n{errorMsg}
            </p>
            <button onClick={handleRetry} style={{
              width: '100%', height: 50, borderRadius: 16, border: 'none',
              background: 'var(--c-primary)', color: 'white',
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginBottom: 10
            }}>
              <RotateCcw size={15} /> 重新上传
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
